/**
 * AccountDialog — créer / modifier / supprimer un compte du plan comptable.
 */
import { useState } from "react";
import { Landmark, Save, Trash2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Field, KIND_LABEL, Modal, TYPE_LABEL, TYPE_ORDER, errMsg, inputCls, useGlBase } from "./_shared";

const TYPE_BY_DIGIT = { 1: "asset", 2: "liability", 3: "equity", 4: "revenue", 5: "expense" };

export default function AccountDialog({ account, onClose, onSaved }) {
  const base = useGlBase();
  const isNew = !account;
  const [f, setF] = useState(account ? { ...account } : { number: "", kind: "A", name: "", type: "", description: "", is_active: true, bank_reconcile: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const onNumber = (v) => { const n = v.replace(/\D/g, "").slice(0, 4); setF((p) => ({ ...p, number: n, type: TYPE_BY_DIGIT[n[0]] || p.type })); };
  const effType = f.type || TYPE_BY_DIGIT[f.number?.[0]] || "";
  const canReconcile = f.kind === "A" && (!effType || effType === "asset" || effType === "liability");

  const save = async () => {
    if (!/^\d{4}$/.test(f.number)) return toast.error("Le numéro doit avoir 4 chiffres");
    if (!f.name.trim()) return toast.error("Nom du compte requis");
    setBusy(true);
    try {
      const payload = { number: f.number, kind: f.kind, name: f.name.trim(), type: f.type || undefined, description: f.description || "", is_active: f.is_active !== false,
        bank_reconcile: canReconcile && !!f.bank_reconcile };
      if (isNew) await api.post(`${base}/accounts`, payload); else await api.put(`${base}/accounts/${account.id}`, payload);
      toast.success(isNew ? "Compte créé" : "Compte mis à jour");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer le compte ${account.number} ${account.name} ?`)) return;
    setBusy(true);
    try { await api.delete(`${base}/accounts/${account.id}`); toast.success("Compte supprimé"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isNew ? "Nouveau compte" : `Compte ${account.number}`} sub="Numérotation à 4 chiffres : 1xxx actif · 2xxx passif · 3xxx capitaux · 4xxx revenus · 5xxx dépenses." onClose={onClose} testId="account-dialog"
      footer={<>
        {!isNew && <Btn tone="danger" icon={Trash2} onClick={remove} busy={busy} disabled={account.has_entries} title={account.has_entries ? "Ce compte a des écritures — désactivez-le plutôt" : ""} testId="account-delete-btn" className="mr-auto">Supprimer</Btn>}
        <Btn tone="slate" onClick={onClose} testId="account-cancel-btn">Annuler</Btn>
        <Btn icon={Save} onClick={save} busy={busy} testId="account-save-btn">{isNew ? "Créer" : "Enregistrer"}</Btn>
      </>}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Numéro *">
          <input value={f.number} onChange={(e) => onNumber(e.target.value)} className={`${inputCls} font-mono`} placeholder="5200" inputMode="numeric" data-testid="account-number-input" />
        </Field>
        <Field label="Genre *" hint="En-tête et Total structurent la liste ; seuls les Comptes reçoivent des écritures.">
          <select value={f.kind} onChange={(e) => set("kind", e.target.value)} className={inputCls} data-testid="account-kind-select">
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{k} — {l}</option>)}
          </select>
        </Field>
        <Field label="Type" hint="Déduit du premier chiffre, modifiable.">
          <select value={f.type || ""} onChange={(e) => set("type", e.target.value)} className={inputCls} data-testid="account-type-select">
            <option value="">Automatique</option>
            {TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Nom *">
        <input value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Fournitures de bureau" maxLength={150} data-testid="account-name-input" />
      </Field>
      <Field label="Description">
        <input value={f.description || ""} onChange={(e) => set("description", e.target.value)} className={inputCls} placeholder="Facultatif" maxLength={300} data-testid="account-description-input" />
      </Field>
      {canReconcile && (
        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer rounded-xl border border-teal-200 bg-teal-50/60 p-3" data-testid="account-bank-reconcile-label">
          <input type="checkbox" checked={!!f.bank_reconcile} onChange={(e) => set("bank_reconcile", e.target.checked)} className="accent-teal-600 mt-0.5" data-testid="account-bank-reconcile-check" />
          <span>
            <span className="font-semibold inline-flex items-center gap-1"><Landmark className="w-3.5 h-3.5 text-teal-700" /> Compte bancaire à rapprocher</span>
            <span className="block text-[11px] text-slate-500 mt-0.5">Encaisse, comptes de banque, carte de crédit, compte Stripe… Seuls les comptes cochés apparaissent dans la tuile Banque (rapprochement bancaire) et dans les virements.</span>
          </span>
        </label>
      )}
      {!isNew && f.kind === "A" && (
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={f.is_active !== false} onChange={(e) => set("is_active", e.target.checked)} className="accent-emerald-600" data-testid="account-active-check" /> Compte actif (décocher pour le masquer des sélecteurs)
        </label>
      )}
    </Modal>
  );
}
